import { BaserowPlugin } from '@baserow/modules/core/plugins'
import SubscriptionMenuItem from '@saas/components/subscriptions/SubscriptionMenuItem.vue'
import SidebarAllWorkspacesSubscriptions from '@saas/components/sidebar/SidebarAllWorkspacesSubscriptions'
import DashboardWorkspacePlanBadge from '@saas/components/dashboard/DashboardWorkspacePlanBadge'
import DashboardWorkspaceRowUsage from '@saas/components/dashboard/DashboardWorkspaceRowUsage'
import AllWorkspacesWorkspacePlanBadge from '@saas/components/allWorkspaces/AllWorkspacesWorkspacePlanBadge'
import AllWorkspacesWorkspaceRowUsage from '@saas/components/allWorkspaces/AllWorkspacesWorkspaceRowUsage'
import AutomationUsageHeaderWarning from '@saas/components/automation/AutomationUsageHeaderWarning'
import WorkflowHistoryUsage from '@saas/components/automation/WorkflowHistoryUsage'
import WorkflowNodeCost from '@saas/components/automation/WorkflowNodeCost'
import ButtonFieldActionCost from '@saas/components/buttonField/ButtonFieldActionCost'
import ButtonFieldActionsCreditInfo from '@saas/components/buttonField/ButtonFieldActionsCreditInfo'
import AdditionalAuthControls from '@saas/components/auth/AdditionalAuthControls'
import PremiumModalButton from '@saas/components/subscriptions/PremiumModalButton'
import App from '@saas/components/App'
import SubscriptionsService from '@saas/services/subscriptions'
import NewsletterService from '@saas/services/newsletter'
import UserService from '@saas/services/user'
import { PremiumPlugin } from '@baserow_premium/plugins'
import { EnterprisePlugin } from '@baserow_enterprise/plugins'
import EnterpriseFeatures from '@baserow_enterprise/features'

export class SaaSPlugin extends BaserowPlugin {
  static getType() {
    return 'saas'
  }

  async userCreated(user, { $store, $client, $posthog }) {
    $posthog?.capture('user_signup', { via_frontend: true })
    $posthog?.startSessionRecording(true)

    // We can safely mark the terms and conditions as being accepted because the
    // user is forced to accept them during signup.
    $store.dispatch('auth/forceUpdateUserData', {
      saas: { accepted_terms_and_conditions: true },
    })

    try {
      await UserService($client).acceptTermsAndConditions(
        JSON.parse(localStorage.getItem('trackedParams') || '{}')
      )
    } catch (e) {}

    if (user.newsletterSignup) {
      try {
        await NewsletterService($client).subscribe(user.email)
        $posthog?.capture('newsletter_signup', { via_frontend: true })
      } catch (error) {}
    }
  }

  async initialWorkspaceCreated(workspace) {
    await SubscriptionsService(this.app.$client).startTrial(workspace.id)
    // This fetches the active licenses again, so that the user immediately has access
    // to free trial features.
    await this.app.$store.dispatch('auth/refresh')
    await this.app.$store.dispatch('saasApp/setShowFreeTrialStep', true)
  }

  getUserContextComponents() {
    return [SubscriptionMenuItem]
  }

  getSidebarAllWorkspacesComponents() {
    return [SidebarAllWorkspacesSubscriptions]
  }

  getDashboardWorkspacePlanBadge() {
    return DashboardWorkspacePlanBadge
  }

  getDashboardWorkspaceRowUsageComponent() {
    return DashboardWorkspaceRowUsage
  }

  getAllWorkspacesWorkspaceBadgeComponents() {
    return [AllWorkspacesWorkspacePlanBadge]
  }

  getAllWorkspacesWorkspaceMetaComponents() {
    return [AllWorkspacesWorkspaceRowUsage]
  }

  getAutomationHeaderComponents(workspace) {
    return [AutomationUsageHeaderWarning]
  }

  getWorkflowHistoryComponents(workflowHistory) {
    return [WorkflowHistoryUsage]
  }

  getAutomationWorkflowNodeContextComponents() {
    return [WorkflowNodeCost]
  }

  getButtonFieldActionTypeContextComponents({ actionType }) {
    // Frontend only actions run in the browser and are not charged.
    return actionType.isFrontendOnly ? [] : [ButtonFieldActionCost]
  }

  getButtonFieldActionListHeadingComponents() {
    return [ButtonFieldActionsCreditInfo]
  }

  async fetchAsyncDashboardData(context, workspaceId) {
    const { $client } = context
    const { data: usageData } =
      await SubscriptionsService($client).getDashboard(workspaceId)

    return { workspaceComponentArguments: { usageData } }
  }

  mergeDashboardData(data, newData) {
    if (
      !data.workspaceComponentArguments ||
      !data.workspaceComponentArguments.usageData
    ) {
      return data
    }

    const usageData = data.workspaceComponentArguments.usageData

    if (
      newData.workspaceComponentArguments &&
      newData.workspaceComponentArguments.usageData
    ) {
      const newUsageData = newData.workspaceComponentArguments.usageData
      for (const workspaceUsageData of newUsageData) {
        const index = usageData.findIndex(
          (ud) => ud.workspace_id === workspaceUsageData.workspace_id
        )
        if (index >= 0) {
          usageData[index] = workspaceUsageData
        } else {
          usageData.push(workspaceUsageData)
        }
      }
    }

    return data
  }

  getRegisterComponent() {
    return AdditionalAuthControls
  }

  getAppLayoutComponent() {
    return App
  }
}

export class SaaSPremiumPlugin extends PremiumPlugin {
  getPremiumModalButtonsComponent() {
    return PremiumModalButton
  }
}

export class SaaSEnterprisePlugin extends EnterprisePlugin {
  getVisuallyHiddenFeatures() {
    return [EnterpriseFeatures.ENTERPRISE_SETTINGS, EnterpriseFeatures.SSO]
  }
}
